import { z } from 'zod'
import {
  type RawLocalPhoto,
  type LocalPhoto,
  type RawRemotePhoto,
  type RemotePhoto,
  type BasePhoto,
  type PetLabel,
} from './PhotoBank.types'
import { getVaryingResolutionImageUrls } from 'components/PhotoWall/cloudinaryTransformations'
import {
  calculatePhotoThumbDimensions,
  getPhotoPreviewUrl,
  isPrivatePhotoUrl,
} from './PhotoBank.utils'
import { PHOTO_PREVIEW_SIZES } from './PhotoBank.consts'

const FileSchema = z.custom<File>((value) => {
  if (typeof File === 'undefined') {
    return true
  }

  return value instanceof File
})

export const DimensionsSchema = z.object({
  width: z.number(),
  height: z.number(),
})

export const ThumbDimensionsSchema = z.object({
  high: DimensionsSchema,
  low: DimensionsSchema,
  minimal: DimensionsSchema,
})

const EmotionsSchema = z.object({
  neutral: z.number(),
  happy: z.number(),
  sad: z.number(),
  angry: z.number(),
  fearful: z.number(),
  disgusted: z.number(),
  surprised: z.number(),
})

export const PhotoFaceDataSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  emotionsScore: EmotionsSchema.nullable().default(null),
  belowCropThreshold: z.boolean().optional(),
})

export const PhotoPetDataSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  score: z.number(),
  label: z.string().transform((label): PetLabel => label),
})

export const FaceDetectionResultSchema = z
  .discriminatedUnion('success', [
    z.object({
      success: z.literal(true),
      faces: z.array(PhotoFaceDataSchema),
    }),
    z.object({ success: z.literal(false) }),
  ])
  .transform((result) =>
    result.success
      ? { success: true, faces: result.faces }
      : { success: false, faces: [] }
  )

const PetDetectionSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('pending') }),
  z.object({
    status: z.literal('detected'),
    pets: z.array(PhotoPetDataSchema),
  }),
  z.object({ status: z.literal('failed') }),
  z.object({ status: z.literal('skipped') }),
])

export const PetDetectionResultSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    pets: z.array(PhotoPetDataSchema),
  }),
  z.object({ success: z.literal(false), error: z.string().nullable() }),
])

const BasePhotoFields = {
  width: z.number(),
  height: z.number(),
  uid: z.string(),
  hashCode: z.string().optional(),
  uploadSrc: z.string().optional(),
  wasUsed: z.boolean().optional().default(false),
  minimalResUrl: z.string().optional(),
  lowResUrl: z.string().optional(),
  highResUrl: z.string().optional(),
  faces: z.array(PhotoFaceDataSchema).optional(),
  petDetection: PetDetectionSchema.default({ status: 'skipped' }),
  smallFacesFiltered: z.number().default(0),
  faceDetectionSuccess: z.boolean().nullable().default(null),
  isStatic: z.boolean().optional(),
}

export const BasePhotoObject = z.object(BasePhotoFields)

export const RemotePhotoSchema = z
  .object({
    ...BasePhotoFields,
    originalUrl: z.string(),
    uploadedAt: z.number().optional(),
    // Photos stored before petDetection existed hold their detected pets here.
    pets: z.array(PhotoPetDataSchema).optional(),
  })
  .transform(({ pets, ...data }) => {
    const url = data.originalUrl
    const resUrlProps = getOrCalculateVaryingResolutionImageUrls(data, url)
    const thumbDimensions =
      data.originalUrl === data.highResUrl
        ? {
            minimal: { width: data.width, height: data.height },
            low: { width: data.width, height: data.height },
            high: { width: data.width, height: data.height },
          }
        : calculatePhotoThumbDimensions(data.width, data.height)

    return {
      ...data,
      ...resUrlProps,
      // A missing upload time means a writer forgot one, not an old photo —
      // defaulting to 0 would make the TTL filters silently delete it.
      uploadedAt: data.uploadedAt ?? Date.now(),
      isLocal: false as const,
      thumbDimensions,
      url,
      faceDetectionSuccess: data.faceDetectionSuccess ?? null,
      smallFacesFiltered: data.smallFacesFiltered ?? 0,
      petDetection: pets
        ? { status: 'detected' as const, pets }
        : data.petDetection,
    }
  })

export const LocalPhotoSchema = z
  .object({
    ...BasePhotoFields,
    localUrl: z.string(),
    originalFile: FileSchema,
    thumbDimensions: ThumbDimensionsSchema,
  })
  .transform((data) => {
    const url = data.localUrl
    const resUrlProps = getOrCalculateVaryingResolutionImageUrls(data, url)

    return {
      ...data,
      ...resUrlProps,
      isLocal: true as const,
      url,
      faceDetectionSuccess: data.faceDetectionSuccess ?? null,
      smallFacesFiltered: data.smallFacesFiltered ?? 0,
    }
  })

export function safeParseRemotePhoto(
  photo: RawRemotePhoto
): z.SafeParseReturnType<unknown, RemotePhoto> {
  return RemotePhotoSchema.safeParse(photo)
}

export function safeParseLocalPhoto(
  photo: RawLocalPhoto
): z.SafeParseReturnType<unknown, LocalPhoto> {
  return LocalPhotoSchema.safeParse(photo)
}

function getOrCalculateVaryingResolutionImageUrls(
  photo: BasePhoto,
  url: string
): {
  minimalResUrl: string
  lowResUrl: string
  highResUrl: string
} {
  let { minimalResUrl, lowResUrl, highResUrl } = photo
  // Saved URLs of a private photo are never trusted (expired presigned URLs, or a Cloudinary fetch of the original):
  // its display URLs are derived from its key
  if (isPrivatePhotoUrl(url)) {
    const minPreviewUrl = getPhotoPreviewUrl(url, PHOTO_PREVIEW_SIZES.MIN)
    return {
      minimalResUrl: minPreviewUrl,
      lowResUrl: minPreviewUrl,
      highResUrl: getPhotoPreviewUrl(url, PHOTO_PREVIEW_SIZES.HIGH),
    }
  }
  if (!minimalResUrl || !lowResUrl || !highResUrl) {
    const resUrls = getVaryingResolutionImageUrls({
      url,
      width: photo.width,
      height: photo.height,
    })
    minimalResUrl = resUrls.minimalResUrl
    lowResUrl = resUrls.lowResUrl
    highResUrl = resUrls.highResUrl
  }

  return {
    minimalResUrl,
    lowResUrl,
    highResUrl,
  }
}
